import React, { useEffect, useMemo, useRef } from "react";
import { Box, Typography } from "@mui/material";
import dayjs from "dayjs";
import MatrixHeaderRow, { LINGUIST_COL_WIDTH } from "./MatrixHeaderRow";
import MatrixRow from "./MatrixRow";
import PeriodNavigator from "../PeriodNavigator";
import { CELL_WIDTH } from "./MatrixCell";

const sortLinguistsByAvailability = (linguists, cells) => {
  const withData = [];
  const withoutData = [];

  linguists.forEach(linguist => {
    const userCells = cells[String(linguist.id)];
    const hasData = userCells && Object.keys(userCells).length > 0;
    (hasData ? withData : withoutData).push(linguist);
  });

  return [...withData, ...withoutData];
};

const AvailabilityMatrix = ({
  dates,
  linguists,
  cells,
  onCellClick,
  readOnly,
  viewMode,
  startDate,
  endDate,
  onViewModeChange,
  onNavigate,
  onToday,
  loading,
}) => {
  const scrollRef = useRef(null);

  const sortedLinguists = useMemo(
    () => sortLinguistsByAvailability(linguists, cells),
    [linguists, cells]
  );

  useEffect(() => {
    if (!dates.length || loading || !scrollRef.current) return;

    const today = dayjs().format("YYYY-MM-DD");
    const todayIndex = dates.indexOf(today);
    if (todayIndex < 0) return;

    const container = scrollRef.current;
    const todayLeft = LINGUIST_COL_WIDTH + todayIndex * CELL_WIDTH;
    const visibleWidth = container.clientWidth - LINGUIST_COL_WIDTH;
    const centeredScroll = todayLeft - LINGUIST_COL_WIDTH - visibleWidth / 2 + CELL_WIDTH / 2;
    const maxScroll = container.scrollWidth - container.clientWidth;

    container.scrollLeft = Math.min(maxScroll, Math.max(0, centeredScroll));
  }, [dates, loading]);

  return (
    <Box>
      <PeriodNavigator
        viewMode={viewMode}
        startDate={startDate}
        endDate={endDate}
        onViewModeChange={onViewModeChange}
        onNavigate={onNavigate}
        onToday={onToday}
        loading={loading}
      />

      {!dates.length ? (
        <Box
          sx={{
            p: 4,
            textAlign: "center",
            border: "1px dashed",
            borderColor: "divider",
            borderRadius: 1,
            bgcolor: "grey.50",
          }}
        >
          <Typography color="text.secondary">
            {loading ? "Loading availability…" : "No dates in the selected period."}
          </Typography>
        </Box>
      ) : !linguists.length ? (
        <Box
          sx={{
            p: 4,
            textAlign: "center",
            border: "1px dashed",
            borderColor: "divider",
            borderRadius: 1,
            bgcolor: "grey.50",
          }}
        >
          <Typography color="text.secondary">No linguists found for the selected filters.</Typography>
        </Box>
      ) : (
        <Box
          ref={scrollRef}
          sx={{
            overflow: "auto",
            maxHeight: "calc(100vh - 120px)",
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 1,
            opacity: loading ? 0.6 : 1,
            transition: "opacity 0.2s",
          }}
        >
          <Box sx={{ display: "inline-block", minWidth: "100%" }}>
            <MatrixHeaderRow dates={dates} />
            {sortedLinguists.map(linguist => (
              <MatrixRow
                key={linguist.id}
                linguist={linguist}
                dates={dates}
                userCells={cells[String(linguist.id)]}
                onCellClick={onCellClick}
                readOnly={readOnly}
              />
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default AvailabilityMatrix;
